import { ReactNode, useState } from 'react';
import { Scrollable } from '@/common/components/scrollable/Scrollable.tsx';
import MenuIcon from '@mui/icons-material/Menu';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import { useTheme } from '@mui/material/styles';
import { useCloseOnNavigation } from '@/common/hooks/useCloseOnNavigation.ts';

const DRAWER_WIDTH = 240;
const MOBILE_APP_BAR_HEIGHT = '56px';

export interface LayoutProps {
  topBarLeft?: ReactNode;
  topBarRight?: ReactNode;
  sidebar?: ReactNode;
  children: ReactNode;
}

export function Layout({ topBarLeft, topBarRight, sidebar, children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useCloseOnNavigation(handleDrawerClose);

  return (
    <Stack sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar position="static" sx={{ zIndex: theme.zIndex.appBar }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            aria-label="open drawer"
            sx={{ mr: 2, display: { xs: 'flex', sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box>{topBarLeft}</Box>
          <Box sx={{ flexGrow: 1 }} minWidth={16} />
          <Box>{topBarRight}</Box>
        </Toolbar>
      </AppBar>

      <Stack direction="row" flex={1}>
        {sidebar && (
          <>
            <Drawer
              open={mobileOpen}
              onClose={handleDrawerClose}
              sx={{
                display: { xs: 'block', md: 'none' },
                width: DRAWER_WIDTH,
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  marginTop: MOBILE_APP_BAR_HEIGHT,
                },
              }}
            >
              <Scrollable>{sidebar}</Scrollable>
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                width: DRAWER_WIDTH,
                position: 'static',
                flexShrink: 1,
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  position: 'static',
                },
              }}
            >
              <Scrollable>{sidebar}</Scrollable>
            </Drawer>
          </>
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
          }}
        >
          <Scrollable>{children}</Scrollable>
        </Box>
      </Stack>
    </Stack>
  );
}
